"use client";

import { useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";

interface FormState {
	// Influencer
	name: string;
	tiktokHandle: string;
	instagramHandle: string;
	youtubeHandle: string;
	redditHandle: string;
	willCreateAccount: boolean;
	// B2B
	firstName: string;
	lastName: string;
	companyName: string;
	phone: string;
	// Shared
	email: string;
	website: string;
	message: string;
}

const initialState: FormState = {
	name: "",
	tiktokHandle: "",
	instagramHandle: "",
	redditHandle: "",
	youtubeHandle: "",
	willCreateAccount: false,
	firstName: "",
	lastName: "",
	companyName: "",
	phone: "",
	email: "",
	website: "",
	message: "",
};

const APPLY_ERROR_CODES = [
	"FIRST_NAME_REQUIRED",
	"LAST_NAME_REQUIRED",
	"EMAIL_INVALID",
	"WEBSITE_INVALID",
	"DUPLICATE_EMAIL",
	"INVALID_APPLICATION",
] as const;

type ApplyErrorCode = (typeof APPLY_ERROR_CODES)[number];

function isApplyErrorCode(value: string): value is ApplyErrorCode {
	return (APPLY_ERROR_CODES as readonly string[]).includes(value);
}

export function AffiliateApplicationForm(props: { initialPartnerType?: "INFLUENCER" | "B2B" }) {
	const t = useTranslations("partners");
	const tErr = useTranslations("partners.applyFormErrors");

	const [formState, setFormState] = useState<FormState>(initialState);
	const [partnerType, setPartnerType] = useState<"INFLUENCER" | "B2B">(
		props.initialPartnerType ?? "INFLUENCER",
	);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const translateApiError = (code: string | undefined): string => {
		if (code && isApplyErrorCode(code)) {
			return tErr(code);
		}
		return t("applyFormErrGeneric");
	};

	const updateField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
		setFormState((current) => ({ ...current, [field]: value }));
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError(null);
		setIsSubmitting(true);

		try {
			const payload =
				partnerType === "INFLUENCER"
					? {
							partnerType,
							name: formState.name,
							email: formState.email,
							tiktokHandle: formState.tiktokHandle,
							instagramHandle: formState.instagramHandle,
							youtubeHandle: formState.youtubeHandle,
							redditHandle: formState.redditHandle,
							website: formState.website,
							willCreateAccount: formState.willCreateAccount,
							message: formState.message,
						}
					: {
							partnerType,
							firstName: formState.firstName,
							lastName: formState.lastName,
							email: formState.email,
							companyName: formState.companyName,
							website: formState.website,
							phone: formState.phone,
							message: formState.message,
						};

			const response = await fetch("/api/affiliate/apply", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			});

			const data = (await response.json()) as { error?: string };

			if (!response.ok) {
				setError(translateApiError(data.error));
				return;
			}

			setSuccess(true);
			setFormState(initialState);
		} catch {
			setError(t("applyFormErrGeneric"));
		} finally {
			setIsSubmitting(false);
		}
	};

	if (success) {
		return (
			<div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
				<h2 className="text-xl font-semibold text-foreground">{t("applyFormSuccessTitle")}</h2>
				<p className="mt-3 text-sm leading-6 text-muted-foreground">{t("applyFormSuccessBody")}</p>
			</div>
		);
	}

	return (
		<form
			onSubmit={handleSubmit}
			className="space-y-6 rounded-2xl border border-border bg-card p-6 shadow-sm"
		>
			<div>
				<h2 className="text-xl font-semibold text-foreground">{t("applyFormTitle")}</h2>
				<p className="mt-2 text-sm leading-6 text-muted-foreground">{t("applyFormSubtitle")}</p>
			</div>

			{error ? (
				<div className="bg-destructive/10 rounded-md p-3 text-sm text-destructive">{error}</div>
			) : null}

			<fieldset className="mb-6">
				<legend className="mb-3 text-sm font-medium text-foreground">{t("applyFormPartnerType")}</legend>
				<div className="flex gap-3">
					{(["INFLUENCER", "B2B"] as const).map((type) => (
						<button
							key={type}
							type="button"
							onClick={() => setPartnerType(type)}
							className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
								partnerType === type
									? "bg-primary/10 border-primary text-primary"
									: "border-border text-muted-foreground hover:border-foreground"
							}`}
						>
							{type === "INFLUENCER" ? t("applyFormTypeInfluencer") : t("applyFormTypeB2b")}
						</button>
					))}
				</div>
			</fieldset>

			{partnerType === "INFLUENCER" ? (
				<InfluencerFields formState={formState} updateField={updateField} t={t} />
			) : (
				<B2BFields formState={formState} updateField={updateField} t={t} />
			)}

			<FormField label={t("applyFormMessage")} htmlFor="message">
				<textarea
					id="message"
					value={formState.message}
					onChange={(event) => updateField("message", event.target.value)}
					rows={6}
					className="focus-visible:outline-hidden flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
				/>
			</FormField>

			<Button type="submit" className="w-full md:w-auto" disabled={isSubmitting}>
				{isSubmitting ? t("applyFormSubmitting") : t("applyFormSubmit")}
			</Button>
		</form>
	);
}

type UpdateField = <K extends keyof FormState>(field: K, value: FormState[K]) => void;
type TFn = ReturnType<typeof useTranslations<"partners">>;

function InfluencerFields({
	formState,
	updateField,
	t,
}: {
	formState: FormState;
	updateField: UpdateField;
	t: TFn;
}) {
	return (
		<div className="space-y-5">
			<div className="grid gap-4 md:grid-cols-2">
				<FormField label={t("applyFormName")} htmlFor="name">
					<Input
						id="name"
						value={formState.name}
						onChange={(e) => updateField("name", e.target.value)}
						placeholder={t("applyFormNamePlaceholder")}
						required
					/>
					<p className="mt-1 text-xs text-muted-foreground">{t("applyFormNameHint")}</p>
				</FormField>
				<FormField label={t("applyFormEmail")} htmlFor="email">
					<Input
						id="email"
						type="email"
						value={formState.email}
						onChange={(e) => updateField("email", e.target.value)}
						required
					/>
				</FormField>
			</div>

			<div className="border-primary/20 bg-primary/5 rounded-xl border p-5">
				<p className="text-sm font-medium text-foreground">{t("applyFormSocialLabel")}</p>
				<p className="mt-1 text-xs text-muted-foreground">{t("applyFormSocialHint")}</p>

				<div className="mt-4 grid gap-3 sm:grid-cols-2">
					<SocialField
						label={t("applyFormTikTok")}
						htmlFor="tiktokHandle"
						value={formState.tiktokHandle}
						onChange={(v) => updateField("tiktokHandle", v)}
						placeholder={t("applyFormHandlePlaceholder")}
						emoji="📱"
					/>
					<SocialField
						label={t("applyFormInstagram")}
						htmlFor="instagramHandle"
						value={formState.instagramHandle}
						onChange={(v) => updateField("instagramHandle", v)}
						placeholder={t("applyFormHandlePlaceholder")}
						emoji="📸"
					/>
					<SocialField
						label={t("applyFormYouTube")}
						htmlFor="youtubeHandle"
						value={formState.youtubeHandle}
						onChange={(v) => updateField("youtubeHandle", v)}
						placeholder={t("applyFormHandlePlaceholder")}
						emoji="🎬"
					/>
					<SocialField
						label={t("applyFormReddit")}
						htmlFor="redditHandle"
						value={formState.redditHandle}
						onChange={(v) => updateField("redditHandle", v)}
						placeholder="u/username"
						emoji="💬"
					/>
					<SocialField
						label={t("applyFormWebsite")}
						htmlFor="influencerWebsite"
						value={formState.website}
						onChange={(v) => updateField("website", v)}
						placeholder="https://"
						emoji="🌐"
					/>
				</div>

				<label
					htmlFor="willCreateAccount"
					className="hover:bg-secondary/50 mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-dashed border-border bg-background p-3"
				>
					<input
						id="willCreateAccount"
						type="checkbox"
						checked={formState.willCreateAccount}
						onChange={(e) => updateField("willCreateAccount", e.target.checked)}
						className="mt-0.5 h-4 w-4 shrink-0"
					/>
					<div>
						<p className="text-sm font-medium text-foreground">{t("applyFormWillCreate")}</p>
						<p className="text-xs text-muted-foreground">{t("applyFormWillCreateHint")}</p>
					</div>
				</label>
			</div>
		</div>
	);
}

function B2BFields({
	formState,
	updateField,
	t,
}: {
	formState: FormState;
	updateField: UpdateField;
	t: TFn;
}) {
	return (
		<div className="grid gap-4 md:grid-cols-2">
			<FormField label={t("applyFormFirstName")} htmlFor="firstName">
				<Input
					id="firstName"
					value={formState.firstName}
					onChange={(e) => updateField("firstName", e.target.value)}
					required
				/>
			</FormField>
			<FormField label={t("applyFormLastName")} htmlFor="lastName">
				<Input
					id="lastName"
					value={formState.lastName}
					onChange={(e) => updateField("lastName", e.target.value)}
					required
				/>
			</FormField>
			<FormField label={t("applyFormEmail")} htmlFor="email">
				<Input
					id="email"
					type="email"
					value={formState.email}
					onChange={(e) => updateField("email", e.target.value)}
					required
				/>
			</FormField>
			<FormField label={t("applyFormCompany")} htmlFor="companyName">
				<Input
					id="companyName"
					value={formState.companyName}
					onChange={(e) => updateField("companyName", e.target.value)}
				/>
			</FormField>
			<FormField label={t("applyFormWebsite")} htmlFor="website">
				<Input
					id="website"
					value={formState.website}
					onChange={(e) => updateField("website", e.target.value)}
					placeholder={t("applyFormWebsitePlaceholder")}
				/>
			</FormField>
			<FormField label={t("applyFormPhone")} htmlFor="phone">
				<Input id="phone" value={formState.phone} onChange={(e) => updateField("phone", e.target.value)} />
			</FormField>
		</div>
	);
}

function SocialField({
	label,
	htmlFor,
	value,
	onChange,
	placeholder,
	emoji,
}: {
	label: string;
	htmlFor: string;
	value: string;
	onChange: (v: string) => void;
	placeholder: string;
	emoji: string;
}) {
	return (
		<div className="flex items-center gap-2">
			<span className="text-xl" aria-hidden>
				{emoji}
			</span>
			<div className="min-w-0 flex-1 space-y-1">
				<Label htmlFor={htmlFor} className="text-xs">
					{label}
				</Label>
				<Input
					id={htmlFor}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					placeholder={placeholder}
					className="text-sm"
				/>
			</div>
		</div>
	);
}

function FormField(props: { htmlFor: string; label: string; children: ReactNode }) {
	return (
		<div className="space-y-2">
			<Label htmlFor={props.htmlFor}>{props.label}</Label>
			{props.children}
		</div>
	);
}
